// @flow

import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useForm, useWatch } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';
import {
  formatCurrency,
  formatDateDB,
} from '@fortress-technology-solutions/fortress-component-library/utils';

import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import useRd356010FormQuery from '../../../hooks/data-fetching/useRd356010FormQuery';
import useGenerateRd356010Mutation from '../../../hooks/data-modifying/useGenerateRd356010Mutation';
import { formatCalendarDateUtc } from '../../../utils/date-helpers';
import messages from './messages';
import {
  buildGridLayout,
  findAgingMismatches,
  toFormValues,
  toGeneratePayload,
  totalsToUpdate,
} from './utils';
import type {
  Customize356010ModalViewModel,
  GridLayout,
  Rd356010Form,
  Rd356010Period,
} from './types';

// The api helpers reject with a string, `{ networkError }` or an object with
// `error` / `message`, not always an Error.
const toErrorText = (error: mixed): string => {
  if (typeof error === 'string') {
    return error;
  }
  const value: Object = error ?? {};
  return String(
    value.error ?? value.message ?? value.networkError ?? '',
  ).trim();
};

const EMPTY_LAYOUT: GridLayout = { sections: [], fieldsByLineId: {} };

export const useCustomize356010Modal = ({
  onClose,
}: {
  onClose: () => void,
}): Customize356010ModalViewModel => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty() ?? {};
  const organizationId = selectedProperty.organizationId ?? '';
  const propertyId = selectedProperty.id ?? '';
  const propertyName = selectedProperty.name ?? '';

  const [asOfDate, setAsOfDate] = useState(() => moment());
  // The date whose Intacct data is loaded; changes only on Refresh.
  const [loadedDate, setLoadedDate] = useState(() => formatDateDB(moment()));

  const query = useRd356010FormQuery({
    organizationId,
    propertyId,
    asOfDate: loadedDate,
  });
  // The form being edited. Only the first load and an explicit Refresh replace
  // it: the app invalidates every query when a page comes back from the
  // back/forward cache, and that refetch must not wipe the user's edits. The
  // snapshot belongs to one property; browser history can switch the selected
  // property without remounting this modal, and another property's balances
  // must never be generated under this one.
  const acceptNextLoad = useRef(true);
  const [loaded, setLoaded] = useState<{
    form: ?Rd356010Form,
    at: number,
    propertyId: string,
  }>({ form: null, at: 0, propertyId });
  const form = loaded.propertyId === propertyId ? loaded.form : null;
  useEffect(() => {
    acceptNextLoad.current = true;
  }, [propertyId]);
  useEffect(() => {
    if (query.data && acceptNextLoad.current) {
      acceptNextLoad.current = false;
      setLoaded({ form: query.data, at: query.dataUpdatedAt, propertyId });
    }
  }, [query.dataUpdatedAt]);
  // A failed Refresh keeps the current edits, so a later refetch nobody asked
  // for must not replace them either.
  useEffect(() => {
    if (query.isError && form) {
      acceptNextLoad.current = false;
    }
  }, [query.errorUpdatedAt]);

  const useFormReturn = useForm({ mode: 'onChange' });
  const { control, getValues, handleSubmit, reset, setValue } = useFormReturn;
  const values = useWatch({ control });

  const layout = useMemo(
    () =>
      form
        ? buildGridLayout(form.lines, {
            assets: intl.formatMessage(messages.assets),
            liabilities: intl.formatMessage(messages.liabilities),
          })
        : EMPTY_LAYOUT,
    [form],
  );

  // Every accepted load (first open or Refresh) replaces whatever was edited.
  useEffect(() => {
    if (form) {
      reset(toFormValues(form, layout));
    }
  }, [loaded.at]);

  // Live totals: read-only cells recomputed whenever a detail cell changes.
  useEffect(() => {
    if (!form) {
      return;
    }
    totalsToUpdate(form.lines, layout, getValues()).forEach(
      ({ fieldName, value }) => {
        setValue(fieldName, value, { shouldDirty: false });
      },
    );
  }, [values, form, layout]);

  const columnLabels = useMemo(
    () => ({
      currentYear: intl.formatMessage(messages.currentYear),
      priorYear: intl.formatMessage(messages.priorYear),
    }),
    [],
  );

  const agingWarnings = form
    ? findAgingMismatches(form.lines, layout, values ?? {}).map((mismatch) =>
        intl.formatMessage(messages.agingMismatch, {
          lineId: mismatch.lineId,
          column: columnLabels[mismatch.column],
          sublineTotal: formatCurrency(mismatch.sublineTotal),
          lineTotal: formatCurrency(mismatch.lineTotal),
        }),
      )
    : [];

  const formatPeriod = (period: ?Rd356010Period): string =>
    period
      ? intl.formatMessage(messages.period, {
          beginDate: formatCalendarDateUtc(period.beginDate) ?? '',
          endDate: formatCalendarDateUtc(period.endDate) ?? '',
        })
      : '';
  const currentYearRange = formatPeriod(form?.header.currentYear);
  const priorYearRange = formatPeriod(form?.header.priorYear);

  const columns = useMemo(
    () => [
      {
        label: intl.formatMessage(messages.line),
        flexSize: 2,
        textAlign: 'left',
        sticky: true,
      },
      {
        label: intl.formatMessage(messages.identify),
        key: 'identifyLabel',
        flexSize: 1,
        textAlign: 'left',
        type: 'text',
      },
      {
        label: columnLabels.currentYear,
        subLabel: currentYearRange,
        key: 'currentYear',
        flexSize: 1,
        type: 'number-currency',
      },
      {
        label: columnLabels.priorYear,
        subLabel: priorYearRange,
        key: 'priorYear',
        flexSize: 1,
        type: 'number-currency',
      },
      {
        label: intl.formatMessage(messages.comments),
        key: 'comment',
        flexSize: 1,
        textAlign: 'left',
        placeholder: intl.formatMessage(messages.commentPlaceholder),
        type: 'text',
      },
    ],
    [currentYearRange, priorYearRange],
  );

  const generateMutation = useGenerateRd356010Mutation({
    organizationId,
    propertyId,
    propertyName,
    options: {
      onSuccess: () => {
        toastr.success(
          intl.formatMessage(messages.success),
          intl.formatMessage(messages.generateSuccess),
        );
      },
      onError: (error) => {
        toastr.error(
          intl.formatMessage(messages.error),
          intl.formatMessage(messages.generateError, {
            error: toErrorText(error),
          }),
        );
      },
    },
  });

  const onRefresh = () => {
    const requested = formatDateDB(asOfDate);
    acceptNextLoad.current = true;
    if (requested === loadedDate) {
      query.refetch();
    } else {
      setLoadedDate(requested);
    }
  };

  const onGenerate = handleSubmit((formValues) => {
    if (form) {
      generateMutation.mutate(toGeneratePayload(form, layout, formValues));
    }
  });

  const isLoading = query.isLoading || query.isFetching;

  return {
    title: [intl.formatMessage(messages.title), propertyName]
      .filter(Boolean)
      .join(' - '),
    asOfDate,
    onAsOfDateChange: (date) => setAsOfDate(date ? moment(date) : moment()),
    form,
    isLoading,
    isError: query.isError,
    errorMessage: intl.formatMessage(messages.loadError, {
      error: toErrorText(query.error),
    }),
    useFormReturn,
    columns,
    sections: layout.sections,
    agingWarnings,
    currentYearRange,
    priorYearRange,
    isGenerating: generateMutation.isLoading,
    canGenerate: Boolean(form) && !isLoading && !query.isError,
    onRefresh,
    onGenerate,
    onClose,
  };
};
